import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EditorModule } from '@tinymce/tinymce-angular';
import { forkJoin } from 'rxjs';

import { Organisme } from 'src/app/data/intervenant/models/organisme.model';
import { OrganismesService } from 'src/app/data/intervenant/services/organismes/organismes.service';
import { MailBalise } from 'src/app/data/mail/models/mail-balise.model';
import { MailCategorieBalise } from 'src/app/data/mail/models/mail-categorie-balise.model';
import { MailTemplate } from 'src/app/data/mail/models/mail-template.model';
import { ParamEmailsService } from 'src/app/data/mail/services/param-emails.service';

@Component({
  selector: 'app-param-emails',
  templateUrl: './param-emails.component.html',
  styleUrl: './param-emails.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    EditorModule
  ]
})
export class ParamEmailsComponent implements OnInit {

  templates: MailTemplate[] = [];
  balises: MailBalise[] = [];
  categorieBalises: MailCategorieBalise[] = [];
  organismes: Organisme[] = [];

  selectedCategorie: MailCategorieBalise | null = null;
  selectedDocument: MailTemplate | null = null;
  selectedOrganisme: Organisme | null = null;

  editorContent = '';

  firstLoad = true;
  loaded = false;
  disableEditor = true;
  isCreationMode = false;

  tinyMceConfig: any;
  tinymce: any;

  private readonly paramEmailsService = inject(ParamEmailsService);
  private readonly organismesService = inject(OrganismesService);

  ngOnInit(): void {
    this.initTinyMce();

    this.firstLoad = true;
    this.loaded = false;

    forkJoin([
      this.paramEmailsService.getTemplates(),
      this.organismesService.getOrganismes(),
      this.paramEmailsService.getBalises(),
      this.paramEmailsService.getCategorieBalises()
    ]).subscribe({
      next: ([templates, organismes, balises, categorieBalises]) => {
        this.templates = templates;
        this.organismes = organismes;
        this.balises = balises;
        this.categorieBalises = categorieBalises;

        this.firstLoad = false;
        this.loaded = true;

        console.log('Données chargées :', {
          templates,
          organismes,
          balises,
          categorieBalises
        });
      },
      error: error => {
        console.error('Erreur lors du chargement des données emails :', error);

        this.firstLoad = false;
        this.loaded = false;
      }
    });
  }

  private initTinyMce(): void {
    this.tinyMceConfig = {
      height: 600,
      menubar: false,
      plugins: [
        'advlist',
        'autolink',
        'lists',
        'link',
        'image',
        'charmap',
        'preview',
        'anchor',
        'searchreplace',
        'visualblocks',
        'code',
        'fullscreen',
        'insertdatetime',
        'media',
        'table',
        'help',
        'wordcount',
        'pagebreak'
      ],
      toolbar:
        'undo redo | ' +
        'blocks fontfamily fontsize | ' +
        'lineheight | ' +
        'bold underline italic forecolor backcolor | ' +
        'table | ' +
        'alignleft aligncenter alignright alignjustify | ' +
        'pagebreak | ' +
        'bullist numlist outdent indent | ' +
        'removeformat | image | code | help',

      language: 'fr-FR',
      table_header_type: 'sectionCells',

      font_size_formats: '7pt 8pt 10pt 12pt 14pt 18pt 24pt 36pt',
      lineheight_formats: '0.5 0.8 1 1.1 1.2 1.3 1.4 1.5 2',
      pagebreak_separator: '<br><div style="page-break-after:always;"></div>',

      extended_valid_elements: 'span[class|style|th:text],p[class|style|th:text],div[class|style|th:text]',

      setup: (editor: any) => {
        this.tinymce = editor;
      }
    };
  }

  get templatesFiltres(): MailTemplate[] {
    if (!this.selectedCategorie) {
      return [];
    }

    return this.templates.filter(template =>
      template.idRefCategorie === this.selectedCategorie?.id
    );
  }

  get balisesFiltrees(): MailBalise[] {
    if (!this.selectedCategorie) {
      return [];
    }

    return this.balises.filter(balise =>
      balise.idRefCategorie === this.selectedCategorie?.id
    );
  }

  get organismesFiltres(): Organisme[] {
    return this.organismes;
  }

  onCategorieChange(): void {
    this.isCreationMode = false;
    this.selectedDocument = null;
    this.selectedOrganisme = null;
    this.editorContent = '';
    this.disableEditor = true;

    if (this.tinymce) {
      this.tinymce.setContent('');
    }
  }

  onDocumentChange(): void {
    this.isCreationMode = false;

    if (!this.selectedDocument) {
      this.editorContent = '';
      this.disableEditor = true;
      return;
    }

    this.editorContent = this.selectedDocument.template ?? '';
    this.disableEditor = false;

    if (this.selectedDocument.idOrganisme) {
      this.selectedOrganisme =
        this.organismes.find(o => o.id === this.selectedDocument?.idOrganisme) ?? null;
    } else {
      this.selectedOrganisme = null;
    }

    if (this.tinymce) {
      this.tinymce.setContent(this.editorContent);
    }
  }

  insererBalise(balise: MailBalise): void {
    if (!this.tinymce || this.disableEditor) {
      return;
    }

    const baliseHtml = this.creerBaliseHtml(balise);

    this.tinymce.insertContent(baliseHtml);
    this.editorContent = this.tinymce.getContent();
  }

  private creerBaliseHtml(balise: MailBalise): string {
    return `<span class="mail-balise" th:text="\${${balise.code}}">[${balise.libelle}]</span>&nbsp;`;
  }

  enregistrerTemplate(): void {
    if (!this.selectedDocument || !this.selectedCategorie || !this.tinymce) {
      return;
    }

    const contenuTinyMce = this.tinymce.getContent();
    const contenu = this.wrapWithThymeleafHtml(contenuTinyMce);

    const payload: MailTemplate = {
      ...this.selectedDocument,
      libelle: this.selectedDocument.libelle,
      template: contenu,
      idRefCategorie: this.selectedCategorie.id,
      idOrganisme: this.selectedOrganisme?.id ?? null
    };

    if (this.isCreationMode) {
      this.paramEmailsService.createTemplate(payload).subscribe({
        next: createdTemplate => {
          this.templates = [...this.templates, createdTemplate];

          this.selectedDocument = createdTemplate;
          this.editorContent = createdTemplate.template;
          this.isCreationMode = false;

          if (this.tinymce) {
            this.tinymce.setContent(createdTemplate.template ?? '');
          }
        }
      });

      return;
    }

    if (!payload.id) {
      return;
    }

    this.paramEmailsService.updateTemplate(payload.id, payload).subscribe({
      next: updatedTemplate => {
        console.log('payload', payload);
        console.log('updatedTemplate', updatedTemplate);

        const templateToUse = updatedTemplate ?? payload;

        const index = this.templates.findIndex(t => t.id === templateToUse.id);

        if (index !== -1) {
          this.templates[index] = templateToUse;
        }

        this.selectedDocument = templateToUse;
        this.editorContent = templateToUse.template ?? '';

        if (this.tinymce) {
          this.tinymce.setContent(templateToUse.template ?? '');
        }
      },
      error: error => {
        console.error('Erreur lors de la mise à jour du template :', error);
      }
    });
  }

  annulerModification(): void {
    if (this.isCreationMode) {
      this.isCreationMode = false;
      this.selectedDocument = null;
      this.selectedOrganisme = null;
      this.editorContent = '';
      this.disableEditor = true;

      if (this.tinymce) {
        this.tinymce.setContent('');
      }

      return;
    }

    if (!this.selectedDocument) {
      return;
    }

    this.editorContent = this.selectedDocument.template ?? '';

    if (this.tinymce) {
      this.tinymce.setContent(this.editorContent);
    }
  }

  nouveauTemplate(): void {
    this.isCreationMode = true;
    this.disableEditor = false;

    this.selectedDocument = {
      code: '',
      libelle: '',
      template: '',
      idRefCategorie: this.selectedCategorie?.id ?? 0,
      idOrganisme: null
    };

    this.selectedOrganisme = null;
    this.editorContent = '';

    if (this.tinymce) {
      this.tinymce.setContent('');
    }
  }

  private wrapWithThymeleafHtml(bodyContent: string): string {
    return `<html xmlns:th="http://www.thymeleaf.org">
              <body>
                ${bodyContent}
              </body>
            </html>`;
  }

}